/**
 * Player chrome — the playbar, fullscreen behaviour and glass telemetry rail,
 * independent of which engine renders the picture.
 *
 * The engine (`viewer/V2Player`, one persistent element) owns decoding;
 * the controls a viewer touches live here so every surface gets the same
 * player UI.
 *
 * The split: this component owns everything that is a CONTROL, the engine owns
 * everything that is MEDIA. Engines expose a `PlayerTransport` (position,
 * duration, play/pause/seek/volume) and render their picture through the
 * `children` render prop, which receives the layout state it needs to size
 * itself (`isFullscreen`, `fitTiles`, `chromeHidden`).
 *
 * Fullscreen is scoped to this container, never to an ancestor: the
 * Maximise→`onMaximize` path lets a shell (mobile landscape) go fullscreen
 * itself, and the player must not then apply its own h-screen chrome over that
 * shell's nav / map / telemetry.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Maximize, Mic, MicOff, Minimize, Play, Pause, Volume2, VolumeX } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { useFullscreen } from '@/lib/fullscreen';
import { cn } from '@/lib/utils';

/**
 * Telemetry cells for the fullscreen-landscape glass rail. The caller
 * supplies playhead-correlated values: speed from the timestamped GPS
 * trail, and — for circuit events — the current lap number,
 * running lap time, best and last lap. Null cells render as an em dash.
 * Non-circuit sessions leave the lap fields null and the rail falls back to
 * the media-time cell.
 */
export interface GlassRailData {
  speedKmh: number | null;
  /** Lap at the current playhead (circuit only; null otherwise). */
  lapNumber: number | null;
  /** Elapsed time in the current lap, ms. */
  runningLapMs: number | null;
  /** Best completed lap, ms. */
  bestLapMs: number | null;
  /** Most recently completed lap before the playhead, ms. */
  lastLapMs: number | null;
}

/** What the chrome needs from an engine to drive its controls. */
export interface PlayerTransport {
  isPlaying: boolean;
  /** Playhead and length on the MEDIA timeline, seconds. */
  currentTime: number;
  duration: number;
  /** False while the media cannot yet respond — play/seek stay disabled. */
  ready: boolean;
  muted: boolean;
  /** 0–1. */
  volume: number;
  togglePlay: () => void;
  seek: (seconds: number) => void;
  setMuted: (muted: boolean) => void;
  setVolume: (volume: number) => void;
}

export interface PlayerChromeLayout {
  isFullscreen: boolean;
  /** Bind the picture by HEIGHT rather than letting a width-driven box scroll. */
  fitTiles: boolean;
  chromeHidden: boolean;
}

export interface PlayerChromeProps {
  transport: PlayerTransport;
  /**
   * `full` (dashboard) keeps the playbar reserved in flow. `minimal` (fan
   * viewer) lets single-surface fullscreen go immersive: the picture fills the
   * frame and the chrome overlays it, auto-hiding while idle.
   */
  variant?: 'full' | 'minimal';
  /**
   * One media surface, so fullscreen can height-bind it. Multi-camera grids
   * pass false — fitting N tiles to full height would oversize them.
   */
  singleSurface?: boolean;
  /** Height-bound the picture without entering fullscreen (mobile landscape). */
  heightFit?: boolean;
  glassRail?: GlassRailData | null;
  /** Fullscreen a parent shell instead of this container; `isMaximized` drives the icon. */
  onMaximize?: () => void;
  isMaximized?: boolean;
  /** Bubbles playbar visibility so a parent can slide its own HUD in lockstep. */
  onControlsHiddenChange?: (hidden: boolean) => void;
  /** Whether the recording has an audio track; null/undefined hides the indicator. */
  audioExpected?: boolean | null;
  children: (layout: PlayerChromeLayout) => React.ReactNode;
}

const IDLE_HIDE_MS = 3000;
/** Below this the volume slider and wide time labels starve the seek track. */
const NARROW_CONTROLS_PX = 460;

const formatTime = (seconds: number): string => {
  if (!isFinite(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/** Lap time, motorsport convention: sub-minute drops the "0:". Mirrors
 *  viewer/LapSelector.formatLapTime so the rail + lap list read alike. */
const formatLapMs = (ms: number): string => {
  const totalSeconds = ms / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds - minutes * 60;
  if (minutes === 0) return seconds.toFixed(1);
  return `${minutes}:${seconds.toFixed(1).padStart(4, '0')}`;
};

/** Says up front whether a recording has sound at all, so silence is not mistaken for a fault. */
const AudioExpectedChip: React.FC<{ expected: boolean; compact: boolean; onDark: boolean }> = ({
  expected,
  compact,
  onDark,
}) => {
  const label = expected ? 'Audio' : 'No audio';
  const title = expected
    ? 'This recording has audio'
    : 'Audio recording was turned off on the phone for this recording';
  return (
    <span
      role="img"
      aria-label={title}
      title={title}
      className={cn(
        'flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
        expected ? 'border-border text-fg-secondary' : 'border-status-warning text-status-warning',
        onDark && expected && 'border-white/40 text-white',
      )}
    >
      {expected ? <Mic className="size-3.5" /> : <MicOff className="size-3.5" />}
      {/* "No audio" is the case a viewer needs spelled out, so it keeps its words on narrow players. */}
      {(!compact || !expected) && <span>{label}</span>}
    </span>
  );
};

export const PlayerChrome: React.FC<PlayerChromeProps> = ({
  transport,
  variant = 'full',
  singleSurface = true,
  heightFit = false,
  glassRail = null,
  onMaximize,
  isMaximized = false,
  onControlsHiddenChange,
  audioExpected = null,
  children,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const { active: isFullscreen, toggle: handleFullscreen } = useFullscreen(containerRef);
  const [chromeHidden, setChromeHidden] = useState(false);
  const [controlsH, setControlsH] = useState(0);
  const [controlsNarrow, setControlsNarrow] = useState(false);

  // The rail is a fullscreen-LANDSCAPE affordance.
  const [isLandscape, setIsLandscape] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(orientation: landscape)').matches
      : true,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(orientation: landscape)');
    const onChange = (e: MediaQueryListEvent) => setIsLandscape(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const fitFullscreen = isFullscreen && singleSurface;
  const fitTiles = fitFullscreen || (heightFit && singleSurface);
  // Immersive fan fullscreen: drop the reserved padding so the height-fit
  // picture isn't squeezed into a small box with a black bottom band and wide
  // side bars. The dashboard keeps its reserved, persistent controls.
  const fillFullscreen = fitFullscreen && variant === 'minimal';
  const railEnabled = glassRail != null;
  // Idle auto-hide whenever the chrome would cover the picture (fill) or to
  // coordinate the rail's settle animation (rail present). In mobile landscape
  // we float and idle-hide even without browser fullscreen: a persistent bar
  // both wastes the short height and sits on top of the telemetry HUD.
  const chromeAutoHides = fillFullscreen || railEnabled;
  const overlayControls = isFullscreen || heightFit;
  const controlsAutoHide = (chromeAutoHides && isFullscreen) || heightFit;

  const hideTimerRef = useRef<number | null>(null);
  const armHideTimer = useCallback(() => {
    if (hideTimerRef.current != null) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => setChromeHidden(true), IDLE_HIDE_MS);
  }, []);
  const { isPlaying } = transport;
  useEffect(() => {
    if (!controlsAutoHide || !isPlaying) {
      if (hideTimerRef.current != null) window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
      setChromeHidden(false);
      return;
    }
    const el = containerRef.current;
    if (!el) return;
    const onActivity = () => {
      setChromeHidden(false);
      armHideTimer();
    };
    armHideTimer();
    el.addEventListener('pointermove', onActivity);
    el.addEventListener('pointerdown', onActivity);
    el.addEventListener('touchstart', onActivity, { passive: true });
    return () => {
      el.removeEventListener('pointermove', onActivity);
      el.removeEventListener('pointerdown', onActivity);
      el.removeEventListener('touchstart', onActivity);
      if (hideTimerRef.current != null) window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    };
  }, [controlsAutoHide, isPlaying, armHideTimer]);

  useEffect(() => {
    onControlsHiddenChange?.(controlsAutoHide ? chromeHidden : false);
  }, [chromeHidden, controlsAutoHide, onControlsHiddenChange]);

  // Container-aware playbar: the player can render inside a 400px rail on a
  // desktop viewport, so viewport breakpoints are the wrong axis. Measure the
  // bar itself.
  useEffect(() => {
    const el = controlsRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => setControlsNarrow(el.offsetWidth < NARROW_CONTROLS_PX);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The rail anchors to the playbar: bottom = controls height while the chrome
  // shows, 0 once it hides.
  useEffect(() => {
    if (!railEnabled || !isFullscreen) return;
    const el = controlsRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => setControlsH(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [railEnabled, isFullscreen]);

  const railCells = [
    {
      label: 'km/h',
      value: glassRail?.speedKmh != null ? String(Math.round(glassRail.speedKmh)) : '—',
    },
    // Circuit: lap cells (playhead-correlated). Otherwise the media-time cell.
    ...(glassRail?.lapNumber != null
      ? [
          { label: 'lap', value: String(glassRail.lapNumber) },
          {
            label: 'this lap',
            value:
              glassRail.runningLapMs != null ? formatLapMs(glassRail.runningLapMs) : '—',
          },
          {
            label: 'best',
            value: glassRail.bestLapMs != null ? formatLapMs(glassRail.bestLapMs) : '—',
          },
          {
            label: 'last',
            value: glassRail.lastLapMs != null ? formatLapMs(glassRail.lastLapMs) : '—',
          },
        ]
      : [{ label: 'time', value: formatTime(transport.currentTime) }]),
  ];

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex flex-grow flex-col',
        // Non-fullscreen height-fit: allow this flex column to shrink below
        // content so the bounded height chain reaches the picture. Fullscreen
        // bounds via h-screen instead.
        fitTiles && !isFullscreen && 'min-h-0',
        isFullscreen && 'h-screen w-screen overflow-hidden bg-black',
        // Reserve room below the picture for the overlaid controls — except in
        // fillFullscreen, where they are auto-hiding overlays and reserving
        // space would only shrink the video. There, pad by the safe-area
        // insets so a notch cannot clip the picture.
        isFullscreen && !fillFullscreen && 'p-4 pb-24',
        fillFullscreen &&
          'pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]',
      )}
    >
      {children({ isFullscreen, fitTiles, chromeHidden })}

      <div
        ref={controlsRef}
        className={cn(
          'mt-2 rounded',
          controlsNarrow ? 'px-2' : 'px-2 sm:px-4',
          // Float the playbar over the picture in fullscreen or mobile
          // landscape. z-20 keeps it above the telemetry HUD.
          overlayControls ? 'absolute bottom-0 left-0 right-0 z-20 bg-black/90 py-3 sm:py-4' : '',
          // The playbar slides out via translateY while the glass rail settles
          // into its place.
          controlsAutoHide && 'transition-transform duration-[280ms] ease-out',
          controlsAutoHide && chromeHidden && 'translate-y-full',
        )}
      >
        {/* hls-controls: touch devices keep :hover stuck after a tap, which
            left buttons filled (the play button's hover swapped the icon to
            bg-base → invisible Pause on mobile). The global
            `@media (hover:none)` rule in index.css neutralizes the stuck
            hover; the per-button hovers below are also gated to hover-capable
            pointers. */}
        <div
          className={cn('hls-controls flex items-center', controlsNarrow ? 'gap-1' : 'gap-1 sm:gap-2')}
        >
          <Button
            variant="ghost"
            size="icon"
            onClick={transport.togglePlay}
            disabled={!transport.ready}
            className={cn(
              'shrink-0 text-accent [@media(hover:hover)]:hover:bg-accent [@media(hover:hover)]:hover:text-bg-base',
              isFullscreen &&
                'text-white [@media(hover:hover)]:hover:bg-white [@media(hover:hover)]:hover:text-bg-base',
            )}
            aria-label={transport.isPlaying ? 'Pause' : 'Play'}
          >
            {transport.isPlaying ? <Pause /> : <Play />}
          </Button>

          <span
            className={cn(
              'min-w-[35px] text-[0.65rem]',
              !controlsNarrow && 'sm:min-w-[45px] sm:text-xs',
              isFullscreen ? 'text-white' : 'text-fg-secondary',
            )}
          >
            {formatTime(transport.currentTime)}
          </span>
          <div className="min-w-0 flex-grow">
            <Slider
              value={[transport.currentTime]}
              max={transport.duration || 100}
              onValueChange={(vals) => transport.seek(vals[0])}
              disabled={!transport.ready || transport.duration === 0}
              step={0.1}
            />
          </div>
          <span
            className={cn(
              'min-w-[35px] text-[0.65rem]',
              !controlsNarrow && 'sm:min-w-[45px] sm:text-xs',
              isFullscreen ? 'text-white' : 'text-fg-secondary',
            )}
          >
            {formatTime(transport.duration)}
          </span>

          <Button
            variant="ghost"
            size="icon"
            onClick={onMaximize ?? handleFullscreen}
            className={cn('shrink-0', isFullscreen && 'text-white')}
            aria-label="Fullscreen"
          >
            {(onMaximize ? isMaximized : isFullscreen) ? <Minimize /> : <Maximize />}
          </Button>

          <div className="flex shrink-0 items-center gap-1">
            {audioExpected != null && (
              <AudioExpectedChip
                expected={audioExpected}
                compact={controlsNarrow}
                onDark={isFullscreen}
              />
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => transport.setMuted(!transport.muted)}
              className={cn(isFullscreen && 'text-white')}
              aria-label={transport.muted ? 'Unmute' : 'Mute'}
            >
              {transport.muted || transport.volume === 0 ? <VolumeX /> : <Volume2 />}
            </Button>
            {/* Volume slider yields to the seek bar in a narrow container —
                the mute button stays. */}
            <div className={cn('w-20', controlsNarrow ? 'hidden' : 'hidden sm:block')}>
              <Slider
                value={[transport.muted ? 0 : transport.volume]}
                min={0}
                max={1}
                step={0.1}
                onValueChange={(vals) => transport.setVolume(vals[0])}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Glass telemetry rail: 32px frosted overlay in fullscreen landscape.
          pointer-events:none so it never steals the chrome-toggle gesture. */}
      {railEnabled && isFullscreen && isLandscape && (
        <div
          className="pointer-events-none absolute left-0 right-0 z-20 flex items-stretch justify-center"
          style={{
            height: 32,
            bottom: chromeHidden ? 0 : controlsH,
            transition: 'bottom 280ms ease',
            background: 'linear-gradient(to top, rgba(4,8,12,0.62), rgba(4,8,12,0.28))',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderTop: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          {railCells.map((cell, i) => (
            <div
              key={cell.label}
              className={cn(
                'flex items-baseline gap-1.5 self-center px-4',
                i > 0 && 'border-l border-white/10',
              )}
            >
              <span className="whitespace-nowrap font-mono text-[13px] font-bold leading-none text-white">
                {cell.value}
              </span>
              <span className="whitespace-nowrap text-[9px] uppercase tracking-widest text-white/55">
                {cell.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlayerChrome;
